/**
 * ops2026 — Staff log. Every staff action recorded in staff_audit_log:
 * account views and edits, deletions, purge / deletion approvals,
 * support-login links created and used, lead deletions, settings changes.
 * Superadmin-only. Filter by action, search, paginate.
 */
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminStaffLogResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import type { SetView } from '../../app/view-router.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const PAGE_SIZE = 50;

/** "support_login_created" → "Support login created". */
function actionLabel(a: string): string {
  const words = a.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Compact one-line rendering of an entry's details. */
function details(m: Readonly<Record<string, unknown>> | null): string {
  if (m === null) return '';
  return Object.entries(m)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

export function StaffLogView({ setView }: { setView: SetView }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AdminStaffLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setOffset(0), [action, query]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.listStaffLog({ action, q: query, limit: PAGE_SIZE, offset }));
    } catch (err) {
      setError(
        err instanceof ApiError && err.kind === 'forbidden'
          ? 'Superadmin required to read the staff log.'
          : 'Failed to load the staff log.',
      );
    } finally {
      setLoading(false);
    }
  }, [api, action, query, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const control = {
    fontFamily: sansFont,
    fontSize: 13,
    padding: '8px 10px',
    minHeight: 36,
    background: C.surface,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
  };
  const btn = (enabled: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: sansFont,
    fontSize: 12,
    padding: '6px 12px',
    minHeight: 40,
    borderRadius: 3,
    background: 'transparent',
    border: `1px solid ${C.border}`,
    color: enabled ? C.text : C.dim,
    opacity: enabled ? 1 : 0.5,
    cursor: enabled ? 'pointer' : 'not-allowed',
  });
  const th = {
    textAlign: 'left' as const,
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
    color: C.muted,
    padding: '6px 8px',
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap' as const,
  };
  const td = { padding: '8px', fontSize: 12.5, color: C.body, borderBottom: `1px solid ${C.borderSoft ?? C.border}`, verticalAlign: 'top' as const };

  const total = data?.total ?? 0;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>
            Operations · Audit
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Staff log</h1>
        </div>
        <button type="button" onClick={() => void load()} style={btn(true)}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            aria-label="Filter by action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            style={{ ...control, flex: '0 1 240px', minWidth: 0 }}
          >
            <option value="">All actions</option>
            {(data?.actions ?? []).map((a) => (
              <option key={a} value={a}>{actionLabel(a)}</option>
            ))}
          </select>
          <input
            aria-label="Search staff log"
            placeholder="Search staff, account, action or details"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...control, flex: '1 1 220px', minWidth: 0 }}
          />
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
          Every action taken from ops: account views and edits, deletions, approvals, support-login links
          created and used, lead deletions. Merchants never see this log.
        </div>
      </Card>

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}
      {loading && data === null ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {data !== null ? (
        <Card style={{ padding: 0 }}>
          {data.entries.length === 0 ? (
            <div style={{ padding: 16, fontFamily: sansFont, fontSize: 13, color: C.muted }}>No entries match.</div>
          ) : (
            <TableScroll minWidth={900}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont }}>
                <thead>
                  <tr>
                    {['When', 'Staff', 'Action', 'Account', 'Details'].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: monoFont, fontSize: 11 }}>{formatIsoCompact(e.at)}</td>
                      <td style={{ ...td, overflowWrap: 'anywhere', minWidth: 170 }}>{e.staff_email ?? e.staff_id}</td>
                      <td style={{ ...td, color: C.text, whiteSpace: 'nowrap' }}>{actionLabel(e.action)}</td>
                      <td style={td}>
                        {e.account_id !== null ? (
                          <button
                            type="button"
                            onClick={() => setView('account-detail', { accountId: e.account_id as string })}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.text, textDecoration: 'underline', textAlign: 'left', fontSize: 12.5 }}
                          >
                            {e.account_name ?? e.account_id}
                          </button>
                        ) : (
                          <span style={{ color: C.dim }}>—</span>
                        )}
                        {e.site_domain !== null ? (
                          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{e.site_domain}</div>
                        ) : null}
                      </td>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 11, color: C.muted, overflowWrap: 'anywhere', minWidth: 260 }}>
                        {details(e.metadata)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>
      ) : null}

      {data !== null ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            {total === 0 ? '0 entries' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" aria-label="Previous page" disabled={!canPrev || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} style={btn(canPrev)}>
              <ChevronLeft size={13} /> Prev
            </button>
            <button type="button" aria-label="Next page" disabled={!canNext || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)} style={btn(canNext)}>
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
